import { useEffect, useRef, useState } from 'react';
import type { Datei } from '@beholder/shared';
import {
  BILD_EINLESEN_BEZUG,
  SPALTEN_TYP_NAMEN,
  SPALTEN_TYPEN,
  type BildErkennung,
  type BildPunkt,
  type SpaltenTyp,
} from '@beholder/shared/module/mindmap';
import { api, modulPfad } from '../../core/api';
import { Auswahl, Dialog, Knopf, Meldung, useAktion } from '../../core/ui';
import { BildBaumBearbeiten, punkteZaehlen } from './BildVorschau';
import { importVorbereiten, type ImportVorschau } from './export';
import { csvLesen, xlsxLesen } from './format';

const BILD_ENDUNGEN = /\.(png|jpe?g|bmp|dib|gif|webp)$/i;

interface Bild {
  url: string;
  name: string;
  dateiId: string | null;
  erkennung: BildErkennung | null;
  baum: BildPunkt | null;
  schritt: 'hochladen' | 'erkennen' | 'bereit' | 'fehler';
}

type Wohin = 'ziel' | 'neu' | 'punkt';

/**
 * CSV/Excel einlesen – oder ein Bild (Paint-Zeichnung, Foto einer Skizze), das die KI erkennt.
 * Als neue Mindmap oder unter einen Punkt einer bestehenden.
 */
export function ImportDialog({
  offen,
  schliessen,
  projektId,
  ziel,
  personen,
  fertig,
}: {
  offen: boolean;
  schliessen(): void;
  projektId: string;
  /** Bestehende Karte und Elternpunkt; ohne = neue Karte. */
  ziel?: { karteId: string; elternId: string; elternTitel: string; vorhandeneSpalten: string[] };
  personen: string[];
  fertig(karteId: string): void;
}) {
  const [vorschau, setVorschau] = useState<ImportVorschau | null>(null);
  const [name, setName] = useState('');
  const [fehler, setFehler] = useState('');
  const [bild, setBild] = useState<Bild | null>(null);
  const [wohin, setWohin] = useState<Wohin>(ziel ? 'ziel' : 'neu');
  const [mitteAlsPunkt, setMitteAlsPunkt] = useState(true);
  const [punkte, setPunkte] = useState<{ id: string; karteId: string; pfad: string }[] | null>(null);
  const [punktId, setPunktId] = useState('');
  const a = useAktion();
  /** Welches Bild gerade dran ist – späte Antworten zu einem verworfenen Bild werden ignoriert. */
  const aktuell = useRef<string | null>(null);
  const pfad = (rest: string) => modulPfad(projektId, 'mindmap', rest);

  useEffect(() => {
    if (offen) setWohin(ziel ? 'ziel' : 'neu');
  }, [offen, ziel?.elternId]);

  // Punkte aller Mindmaps nur laden, wenn „unter einen Punkt“ gewählt wird
  useEffect(() => {
    if (wohin !== 'punkt' || punkte) return;
    api
      .get<{ id: string; karteId: string; pfad: string }[]>(pfad('knoten'))
      .then((l) => setPunkte([...l].sort((x, y) => x.pfad.localeCompare(y.pfad, 'de'))))
      .catch(() => setPunkte([]));
  }, [wohin, punkte]);

  /** Nicht übernommenes Bild wieder entfernen (sofort endgültig, nicht in den Papierkorb). */
  function bildVerwerfen(b: Bild | null) {
    if (!b) return;
    if (aktuell.current === b.url) aktuell.current = null;
    URL.revokeObjectURL(b.url);
    if (b.dateiId) void api.delete(pfad(`bild/${b.dateiId}`)).catch(() => undefined);
  }

  async function bildErkennen(dateiId: string, b: Bild) {
    setFehler('');
    setBild({ ...b, dateiId, schritt: 'erkennen' });
    try {
      const e = await api.post<BildErkennung>(pfad('bild/erkennen'), { dateiId });
      if (aktuell.current !== b.url) return;
      setBild({ ...b, dateiId, erkennung: e, baum: e.baum, schritt: 'bereit' });
    } catch (f) {
      if (aktuell.current !== b.url) return;
      setBild({ ...b, dateiId, schritt: 'fehler' });
      setFehler(f instanceof Error ? f.message : 'Das Bild konnte nicht erkannt werden.');
    }
  }

  async function bildLesen(datei: File) {
    const b: Bild = { url: URL.createObjectURL(datei), name: datei.name, dateiId: null, erkennung: null, baum: null, schritt: 'hochladen' };
    aktuell.current = b.url;
    setBild(b);
    try {
      const [d] = await api.hochladen<Datei[]>(`/api/projekte/${projektId}/dateien?bereich=mindmap&bezug=${BILD_EINLESEN_BEZUG}`, [datei]);
      if (!d) throw new Error('Das Bild konnte nicht hochgeladen werden.');
      if (aktuell.current !== b.url) {
        // Dialog inzwischen geschlossen oder anderes Bild gewählt
        void api.delete(pfad(`bild/${d.id}`)).catch(() => undefined);
        return;
      }
      await bildErkennen(d.id, b);
    } catch (f) {
      if (aktuell.current !== b.url) return;
      setBild({ ...b, schritt: 'fehler' });
      setFehler(f instanceof Error ? f.message : 'Das Bild konnte nicht hochgeladen werden.');
    }
  }

  async function lesen(datei: File) {
    setFehler('');
    setVorschau(null);
    bildVerwerfen(bild);
    setBild(null);
    if (BILD_ENDUNGEN.test(datei.name) || datei.type.startsWith('image/')) {
      if (/\.svg$/i.test(datei.name) || datei.type === 'image/svg+xml') {
        setFehler('SVG-Zeichnungen lassen sich nicht einlesen – bitte als PNG speichern.');
        return;
      }
      return bildLesen(datei);
    }
    try {
      const endung = datei.name.toLowerCase().split('.').pop();
      let t;
      if (endung === 'xlsx') t = await xlsxLesen(new Uint8Array(await datei.arrayBuffer()));
      else if (endung === 'csv' || endung === 'txt' || endung === 'tsv') {
        const zeilen = csvLesen(await datei.text());
        t = { zeilen, ebenen: zeilen.map(() => 0), einrueckung: zeilen.map(() => []), datumSpalten: new Set<number>() };
      } else
        throw new Error(
          'Bitte eine CSV- oder Excel-Datei (.xlsx) oder ein Bild (PNG, JPG, BMP – z. B. aus Paint) wählen. Alte .xls-Dateien vorher in Excel als .xlsx speichern.',
        );
      const v = importVorbereiten(t, datei.name, personen);
      if (!v.eingabe.zeilen.length) throw new Error('In der Datei wurden keine Zeilen mit Titel gefunden.');
      setVorschau(v);
      setName(datei.name);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Datei konnte nicht gelesen werden.');
    }
  }

  async function einlesen() {
    if (!vorschau) return;
    const r = await a.ausfuehren(() =>
      ziel
        ? api.post<{ karteId: string }>(pfad(`karten/${ziel.karteId}/import`), {
            ...vorschau.eingabe,
            spalten: vorschau.spalten,
            elternId: ziel.elternId,
          })
        : api.post<{ karteId: string }>(pfad('import'), { ...vorschau.eingabe, spalten: vorschau.spalten }),
    );
    if (r) {
      setVorschau(null);
      fertig(r.karteId);
    }
  }

  const gewaehlterPunkt = punkte?.find((p) => p.id === punktId);
  async function bildUebernehmen() {
    if (!bild?.dateiId || !bild.baum) return;
    const unter =
      wohin === 'ziel' && ziel ? { karteId: ziel.karteId, elternId: ziel.elternId } : wohin === 'punkt' && gewaehlterPunkt ? { karteId: gewaehlterPunkt.karteId, elternId: gewaehlterPunkt.id } : null;
    const r = await a.ausfuehren(() =>
      api.post<{ karteId: string }>(pfad('bild/uebernehmen'), {
        dateiId: bild.dateiId,
        baum: bild.baum,
        ...(unter ? { ...unter, mitteAlsPunkt } : {}),
      }),
    );
    if (r) {
      URL.revokeObjectURL(bild.url);
      setBild(null);
      fertig(r.karteId);
    }
  }

  const zu = () => {
    bildVerwerfen(bild);
    setBild(null);
    setVorschau(null);
    setFehler('');
    setPunktId('');
    schliessen();
  };

  const z = vorschau?.eingabe.zeilen ?? [];
  const min = z.length ? Math.min(...z.map((x) => x.ebene)) : 0;
  const neueSpalten = vorschau?.spalten.filter((s) => !ziel?.vorhandeneSpalten.some((v) => v.toLowerCase() === s.name.toLowerCase())) ?? [];
  const bildPunkte = bild?.baum ? punkteZaehlen(bild.baum) : 0;
  const bildBereit = bild?.schritt === 'bereit' && bildPunkte > 0 && (wohin !== 'punkt' || !!gewaehlterPunkt);
  const nurAeste = wohin !== 'neu' && !mitteAlsPunkt;
  const anzahlNeu = nurAeste && bild?.baum ? bildPunkte - (bild.baum.titel.trim() ? 1 : 0) : bildPunkte;

  return (
    <Dialog
      titel={ziel ? `Datei unter „${ziel.elternTitel}“ einfügen` : 'Mindmap aus Datei einlesen'}
      offen={offen}
      schliessen={zu}
      breit
      fuss={
        <>
          {bild ? (
            <Knopf art="haupt" disabled={!bildBereit || a.laeuft || anzahlNeu < 1} onClick={() => void bildUebernehmen()}>
              {a.laeuft ? 'Übernimmt …' : `${anzahlNeu} ${anzahlNeu === 1 ? 'Punkt' : 'Punkte'} übernehmen`}
            </Knopf>
          ) : (
            <Knopf art="haupt" disabled={!vorschau || a.laeuft} onClick={() => void einlesen()}>
              {a.laeuft ? 'Liest ein …' : `${z.length} Zeilen einlesen`}
            </Knopf>
          )}
          <Knopf onClick={zu}>Abbrechen</Knopf>
        </>
      }
    >
      <div className="stapel">
        <p className="leise klein">
          CSV oder Excel (.xlsx): Die erste Zeile enthält die Spaltennamen. Die Ebenen werden aus einer Spalte „Ebene“, aus
          Gliederungsnummern (1.2.3), einem „Pfad“, der Excel-Gruppierung oder der Einrückung des Titels erkannt. Weitere
          Spalten werden zu eigenen Tabellenspalten.
          <br />
          Bild (PNG, JPG, BMP – z. B. aus Paint oder ein Foto einer Handskizze): Die KI erkennt die Punkte, der Begriff in der
          Mitte wird zur Mitte der Mindmap. Vor dem Übernehmen lässt sich alles prüfen und ändern; das Bild bleibt als Anlage
          erhalten.
        </p>
        <input
          type="file"
          accept=".csv,.xlsx,.txt,.tsv,.png,.jpg,.jpeg,.bmp,.dib,.gif,.webp,image/png,image/jpeg,image/bmp,image/gif,image/webp"
          onChange={(e) => {
            const d = e.target.files?.[0];
            e.target.value = '';
            if (d) void lesen(d);
          }}
        />
        <Meldung>{fehler || a.fehler}</Meldung>

        {bild && (
          <div className="mm-bild-einlesen">
            <img src={bild.url} alt={`Vorschau von ${bild.name}`} className="mm-bild-bild" />
            <div className="stapel">
              {bild.schritt === 'hochladen' && <Meldung art="info">„{bild.name}“ wird hochgeladen …</Meldung>}
              {bild.schritt === 'erkennen' && (
                <Meldung art="info">Die KI liest die Zeichnung … Mit lokaler KI kann das eine Minute dauern.</Meldung>
              )}
              {bild.schritt === 'fehler' && bild.dateiId && (
                <div>
                  <Knopf onClick={() => void bildErkennen(bild.dateiId!, bild)}>Noch einmal versuchen</Knopf>
                </div>
              )}
              {bild.schritt === 'bereit' && bild.baum && bild.erkennung && (
                <>
                  <Meldung art="info">
                    {bildPunkte} {bildPunkte === 1 ? 'Punkt' : 'Punkte'} erkannt
                    {bild.erkennung.anbieter === 'claude' ? ' (mit Claude)' : ''}. Bitte prüfen: Titel ändern, mit + ergänzen, mit ×
                    weglassen.
                    {bild.erkennung.hinweis ? ` Hinweis der KI: ${bild.erkennung.hinweis}` : ''}
                  </Meldung>
                  <BildBaumBearbeiten baum={bild.baum} aendern={(b) => setBild({ ...bild, baum: b })} gesperrt={a.laeuft} />
                  <fieldset className="mm-optionen">
                    <legend>Wohin?</legend>
                    {ziel && (
                      <label className="mm-wahl">
                        <input type="radio" name="mm-wohin" checked={wohin === 'ziel'} onChange={() => setWohin('ziel')} /> Unter „{ziel.elternTitel}“
                        einfügen
                      </label>
                    )}
                    <label className="mm-wahl">
                      <input type="radio" name="mm-wohin" checked={wohin === 'neu'} onChange={() => setWohin('neu')} /> Als neue Mindmap „
                      {bild.baum.titel.trim() || 'Zeichnung'}“
                    </label>
                    <label className="mm-wahl">
                      <input type="radio" name="mm-wohin" checked={wohin === 'punkt'} onChange={() => setWohin('punkt')} /> Unter einen anderen Punkt
                    </label>
                    {wohin === 'punkt' && (
                      <Auswahl value={punktId} onChange={(e) => setPunktId(e.target.value)} aria-label="Punkt, unter den eingefügt wird">
                        <option value="">{punkte ? 'Punkt wählen …' : 'Lädt …'}</option>
                        {punkte?.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.pfad}
                          </option>
                        ))}
                      </Auswahl>
                    )}
                    {wohin !== 'neu' && (
                      <label className="mm-wahl">
                        <input type="checkbox" checked={mitteAlsPunkt} onChange={(e) => setMitteAlsPunkt(e.target.checked)} /> Mitte der Zeichnung
                        als eigenen Punkt anlegen {mitteAlsPunkt ? '(das Bild hängt dann an ihr)' : '(nur die Äste; das Bild hängt am Zielpunkt)'}
                      </label>
                    )}
                  </fieldset>
                </>
              )}
            </div>
          </div>
        )}

        {vorschau && (
          <>
            <Meldung art="info">
              {name}: {z.length} Zeilen. {vorschau.hinweis}
            </Meldung>
            <div className="mm-import-vorschau">
              {z.slice(0, 14).map((x, i) => (
                <div key={i} style={{ paddingLeft: (x.ebene - min) * 18 }}>
                  • {x.titel}
                </div>
              ))}
              {z.length > 14 && <div className="leise">… und {z.length - 14} weitere</div>}
            </div>
            {neueSpalten.length > 0 && (
              <fieldset className="mm-optionen">
                <legend>Neue Spalten</legend>
                {neueSpalten.map((s) => (
                  <div key={s.name} className="mm-option-zeile">
                    <span>{s.name}</span>
                    <Auswahl
                      value={s.typ}
                      onChange={(e) =>
                        setVorschau({
                          ...vorschau,
                          spalten: vorschau.spalten.map((x) => (x.name === s.name ? { ...x, typ: e.target.value as SpaltenTyp } : x)),
                        })
                      }
                    >
                      {SPALTEN_TYPEN.map((t) => (
                        <option key={t} value={t}>
                          {SPALTEN_TYP_NAMEN[t]}
                        </option>
                      ))}
                    </Auswahl>
                  </div>
                ))}
              </fieldset>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
